/**
 * 玩家数据模型
 */

import { ITEMS } from '../data/items';
import { findRealmLevel, getRealmDisplayName } from '../data/realms';
import type { SpiritualRoot } from '../data/spiritualRoots';
import { generateSpiritualRoot } from '../data/spiritualRoots';
import type { Origin } from '../data/origins';
import { ORIGINS, generateOrigin } from '../data/origins';
import type { Identity } from '../data/identities';
import { IDENTITIES, assignIdentity } from '../data/identities';
import type { EquipSlot } from '../data/equipment';
import { SLOT_NAMES } from '../data/equipment';
import type { Equipment } from './EquipmentSystem';
import { getFinalStats, generateEquipment } from './EquipmentSystem';
import { getTechniqueMultiplier, assignStartingTechnique } from './TechniqueSystem';

export interface PlayerState {
  name: string;
  realmKey: string;
  cultivationExp: number;
  hp: number;
  maxHp: number;
  mp: number;
  maxMp: number;
  attack: number;
  defense: number;
  spiritStones: number;
  inventory: any[];
  /** 装备槽位（15槽） */
  equipment: Partial<Record<EquipSlot, Equipment>>;
  /** 灵根 */
  spiritualRoot: SpiritualRoot;
  /** 出身背景ID */
  originId: string;
  /** 起始身份ID */
  identityId: string;
  /** 功法 */
  techniqueId: string;
  techniqueLayer: number;
  /** 炼丹经验值 */
  alchemyExp: number;
  /** 炼器经验值 */
  forgeExp: number;
  /** 门派贡献度 */
  sectContribution: number;
  /** 当前门派任务列表 */
  sectMissions: any[];
  /** 悬赏任务 */
  bounty: any | null;
  /** 灵脉占领 */
  territory: { veinBonus: number; veinName: string } | null;
  /** 强制任务（未完成冻结修炼） */
  mandatoryMission: any | null;
  /** 上次强制任务完成时间（12小时CD） */
  mandatoryMissionDoneAt: number;
  /** 经脉：经脉ID → 已通穴位数 */
  meridians: Record<string, number>;
  /** 已解锁成就ID列表 */
  unlockedAchievements: string[];
  /** 特殊buff（如逃脱buff的剩余突破次数） */
  escapeBuffRemaining: number;
  lastSaveTime: number;
  deathCount: number;
  killCount: number;
}

/** 查找玩家出身 */
export function getPlayerOrigin(player: PlayerState): Origin {
  return ORIGINS.find((o) => o.id === player.originId) ?? ORIGINS[2];
}

/** 查找玩家身份 */
export function getPlayerIdentity(player: PlayerState): Identity {
  return IDENTITIES.find((i) => i.id === player.identityId) ?? IDENTITIES[0];
}

/** 创建初始玩家 */
export function createPlayer(name: string): PlayerState {
  const starterRealm = 'qi_refining_1';
  const realmLevel = findRealmLevel(starterRealm)!;
  const mult = realmLevel.statMultiplier;
  const spiritualRoot = generateSpiritualRoot();
  const origin = generateOrigin();
  const identity = assignIdentity(spiritualRoot.grade, origin);

  const player: PlayerState = {
    name,
    realmKey: starterRealm,
    cultivationExp: 0,
    hp: Math.floor(100 * mult),
    maxHp: Math.floor(100 * mult),
    mp: Math.floor(50 * mult),
    maxMp: Math.floor(50 * mult),
    attack: Math.floor(10 * mult),
    defense: Math.floor(5 * mult),
    spiritStones: origin.startingStones + identity.startingStones,
    inventory: [],
    equipment: {},
    spiritualRoot,
    originId: origin.id,
    identityId: identity.id,
    techniqueId: assignStartingTechnique(identity.id),
    techniqueLayer: 1,
    alchemyExp: 0,
    forgeExp: 0,
    sectContribution: 0,
    sectMissions: [],
    bounty: null,
    territory: null,
    mandatoryMission: null,
    mandatoryMissionDoneAt: 0,
    meridians: {},
    unlockedAchievements: [],
    escapeBuffRemaining: identity.escapeBuff?.breakthroughCount ?? 0,
    lastSaveTime: Date.now(),
    deathCount: 0,
    killCount: 0,
  };

  // 添加初始物品
  for (const itemId of [...origin.startingItems, ...identity.startingItems]) {
    const template = findItemById(itemId);
    if (template) player.inventory.push(JSON.parse(JSON.stringify(template)));
  }

  // 添加初始装备（新装备系统）
  if (identity.startingEquipment) {
    for (const eqId of identity.startingEquipment) {
      const equip = generateEquipment(eqId);
      if (equip) player.inventory.push(equip as any);
    }
  }

  // 初始腰带（所有角色都有，被囚除外）
  if (identity.id !== 'imprisoned') {
    const belt = generateEquipment('cloth_belt');
    if (belt) player.equipment['waist'] = belt;
  }

  // 被囚身份初始debuff
  if (identity.startingHpPercent) {
    player.hp = Math.floor(player.maxHp * identity.startingHpPercent);
  }
  if (identity.startingMpPercent !== undefined) {
    player.mp = Math.floor(player.maxMp * identity.startingMpPercent);
  }

  return player;
}

function findItemById(id: string): any {
  return ITEMS.find((i) => i.id === id);
}

/** 获取当前境界的显示名称 */
export function getPlayerRealmDisplay(player: PlayerState): string {
  return getRealmDisplayName(player.realmKey);
}

/** 获取当前境界完整信息 */
export function getPlayerRealmLevel(player: PlayerState) {
  return findRealmLevel(player.realmKey);
}

/** 计算玩家当前境界的修炼进度百分比 (0-100) */
export function getCultivationProgress(player: PlayerState): number {
  const realmLevel = findRealmLevel(player.realmKey);
  if (!realmLevel) return 0;
  return Math.min(100, (player.cultivationExp / realmLevel.expRequired) * 100);
}

/** 是否可以尝试突破 */
export function canBreakthrough(player: PlayerState): boolean {
  return getCultivationProgress(player) >= 100;
}

/** 玩家是否死亡 */
export function isPlayerDead(player: PlayerState): boolean {
  return player.hp <= 0;
}

/** 获取装备槽的中文名 */
export function getSlotName(slot: EquipSlot): string {
  return SLOT_NAMES[slot] ?? slot;
}

/** 获取背包最大容量 */
export function getMaxInventorySize(player: PlayerState): number {
  const belt = player.equipment['waist'] as Equipment | undefined;
  return belt?.capacity ?? 8;
}

/** 装备物品 */
export function equipItem(player: PlayerState, item: Equipment): boolean {
  const slot = item.slot;
  // ring1 items can also go to ring2
  const targetSlot = (slot === 'ring1' && player.equipment['ring1']) ? 'ring2' as EquipSlot : slot;
  if (targetSlot === 'ring2' && player.equipment['ring2'] && player.equipment['ring1']) {
    // both rings occupied, try ring1 first
  }
  const actualSlot = player.equipment[slot] ? (slot === 'ring1' ? 'ring2' as EquipSlot : slot) : slot;
  const old = player.equipment[actualSlot];
  if (old) player.inventory.push(old);
  const idx = player.inventory.findIndex((i: any) => (i as any).id === item.id || (i as any).name === item.name);
  if (idx === -1) return false;
  player.inventory.splice(idx, 1);
  player.equipment[actualSlot] = item;
  if (actualSlot === 'waist') {
    const maxSize = getMaxInventorySize(player);
    while (player.inventory.length > maxSize) player.inventory.pop();
  }
  recalcStats(player);
  return true;
}

/** 卸下装备 */
export function unequipItem(player: PlayerState, slot: EquipSlot): Equipment | null {
  const item = player.equipment[slot];
  if (!item) return null;
  player.equipment[slot] = undefined;
  player.inventory.push(item as any);
  recalcStats(player);
  return item;
}

/** 更新玩家属性（基础 × 境界 × 装备 + 身份加成） */
export function recalcStats(player: PlayerState): void {
  const realmLevel = findRealmLevel(player.realmKey);
  if (!realmLevel) return;
  const mult = realmLevel.statMultiplier;
  const identity = getPlayerIdentity(player);
  const origin = getPlayerOrigin(player);

  // 兼容旧存档
  if (!player.equipment) (player as any).equipment = {};

  let bonusAtk = 0;
  let bonusDef = 0;
  let bonusHp = 0;
  let bonusMp = 0;

  const ALL_SLOTS: EquipSlot[] = ['head','face','neck','body','inner','back','arms','hands','ring1','ring2','waist','legs','feet','weapon','offhand'];
  for (const slot of ALL_SLOTS) {
    const eq = player.equipment[slot] as Equipment | undefined;
    if (eq) {
      const stats = getFinalStats(eq);
      bonusAtk += stats.atk;
      bonusDef += stats.def;
      bonusHp += stats.hp;
      bonusMp += stats.mp;
    }
  }

  let baseMaxHp = Math.floor(100 * mult) + (origin.permHpBonus ?? 0);
  const baseMaxMp = Math.floor(50 * mult);
  let baseAtk = Math.floor(10 * mult) + (origin.permAttackBonus ?? 0);
  let baseDef = Math.floor(5 * mult) + (origin.permDefenseBonus ?? 0);

  // 身份加成（百分比叠加）
  if (identity.permAttackBonus) baseAtk = Math.floor(baseAtk * (1 + identity.permAttackBonus));
  if (identity.permDefBonus) baseDef = Math.floor(baseDef * (1 + identity.permDefBonus));

  player.maxHp = baseMaxHp + bonusHp;
  player.maxMp = baseMaxMp + bonusMp;
  player.attack = baseAtk + bonusAtk;
  player.defense = baseDef + bonusDef;

  player.hp = Math.min(player.hp, player.maxHp);
  player.mp = Math.min(player.mp, player.maxMp);
}

/** 获取修炼速度加成（灵根 + 身份 + 功法 + 逃脱buff） */
export function getExpMultiplier(player: PlayerState): number {
  const rootMult = player.spiritualRoot?.speedMultiplier ?? 1;
  const techMult = getTechniqueMultiplier(player);
  const identity = getPlayerIdentity(player);
  let bonus = identity.permExpBonus ?? 0;
  // 逃脱buff
  if (player.escapeBuffRemaining > 0 && identity.escapeBuff) {
    bonus += identity.escapeBuff.expBonus;
  }
  return rootMult * techMult * (1 + bonus);
}

/** 获取战斗灵石加成 */
export function getCombatStoneMultiplier(player: PlayerState): number {
  const identity = getPlayerIdentity(player);
  return 1 + (identity.combatStoneBonus ?? 0);
}

/** 挂机修炼是否被冻结（门派强制任务未完成） */
export function isCultivationBlocked(player: PlayerState): boolean {
  if (player.mandatoryMission == null) return false;
  // 如果上一次完成时间在12小时内 → 不冻结（CD中）
  const doneAt = player.mandatoryMissionDoneAt ?? 0;
  if (doneAt > 0 && Date.now() - doneAt < 12 * 3600 * 1000) return false;
  // 否则有未完成的任务 → 冻结
  return true;
}

/** 消费逃脱buff次数（突破时调用） */
export function consumeEscapeBuff(player: PlayerState): void {
  if (player.escapeBuffRemaining > 0) {
    player.escapeBuffRemaining--;
  }
}
